import React, { useState, useEffect } from 'react';
import './TimetableManager.css';

const TimetableManager = ({ userRole, userId, userName, autoLoad = false, embedded = false }) => {
  const [timetables, setTimetables] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [currentTimetable, setCurrentTimetable] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState('today'); // today, grid or list - default to daily view
  const [loading, setLoading] = useState(autoLoad);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    if (userRole === 'admin1') {
      fetchAllTimetables();
    }
    if (autoLoad && userRole === 'teacher') {
      fetchTimetable('');
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userRole, autoLoad]);
  
  useEffect(() => {
    if (showMobilePopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobilePopup]);

  const fetchClasses = async () => {
    try {
      const response = await fetch('/api/classes');
      const data = await response.json();
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await fetch('/api/users?role=teacher');
      const data = await response.json();
      setTeachers(data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchAllTimetables = async () => {
    try {
      const response = await fetch(`/api/timetables?userRole=${userRole}`);
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setTimetables(data);
    } catch (error) {
      console.error('Error fetching timetables:', error);
      alert('Error loading timetables: ' + error.message);
    }
  };

  const fetchTimetable = async (className) => {
    try {
      setLoading(true);
      console.log('Fetching timetable for:', className, 'userRole:', userRole, 'userId:', userId);
      
      const url = userRole === 'teacher' 
        ? `/api/teacher-timetable/${userId}`
        : `/api/timetable/${className}?userRole=${userRole}&userId=${userId}`;
      
      console.log('Fetching from URL:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        console.log(`Timetable API returned status: ${response.status}`);
        if (response.status === 404) {
          console.log('No timetable found');
          setCurrentTimetable(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Timetable data received:', data);
      
      if (data && data.error) {
        if (data.error.includes('Access denied')) {
          console.log('Access denied for timetable management');
        }
        throw new Error(data.error);
      }
      
      // For teacher role, convert the teacher timetable format to standard format
      if (userRole === 'teacher' && data && data.schedule) {
        const teacherTimetable = {
          className: `Teaching Schedule`,
          academicYear: '2024-25',
          periods: [
            { name: 'Period 1', startTime: '08:00', endTime: '08:45', duration: 45 },
            { name: 'Period 2', startTime: '08:45', endTime: '09:30', duration: 45 },
            { name: 'Break', startTime: '09:30', endTime: '09:45', duration: 15 },
            { name: 'Period 3', startTime: '09:45', endTime: '10:30', duration: 45 },
            { name: 'Period 4', startTime: '10:30', endTime: '11:15', duration: 45 },
            { name: 'Lunch', startTime: '11:15', endTime: '12:00', duration: 45 },
            { name: 'Period 5', startTime: '12:00', endTime: '12:45', duration: 45 },
            { name: 'Period 6', startTime: '12:45', endTime: '13:30', duration: 45 }
          ],
          subjects: [],
          schedule: data.schedule,
          classes: data.classes || []
        };
        setCurrentTimetable(teacherTimetable);
      } else {
        setCurrentTimetable(data);
      }
      
      if (!data) {
        console.log('No timetable data returned');
        setCurrentTimetable(null);
      }
    } catch (error) {
      console.error('Error fetching timetable:', error);
      setCurrentTimetable(null);
    } finally {
      setLoading(false);
    }
  };

  const createNewTimetable = () => {
    const template = {
      className: selectedClass,
      academicYear: '2024-25',
      periods: [
        { name: 'Period 1', startTime: '08:00', endTime: '08:45', duration: 45 },
        { name: 'Period 2', startTime: '08:45', endTime: '09:30', duration: 45 },
        { name: 'Break', startTime: '09:30', endTime: '09:45', duration: 15 },
        { name: 'Period 3', startTime: '09:45', endTime: '10:30', duration: 45 },
        { name: 'Period 4', startTime: '10:30', endTime: '11:15', duration: 45 },
        { name: 'Lunch', startTime: '11:15', endTime: '12:00', duration: 45 }
      ],
      subjects: [],
      schedule: {
        monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: []
      }
    };
    setCurrentTimetable(template);
    setEditMode(true);
  };

  const saveTimetable = async () => {
    try {
      setLoading(true);
      console.log('Saving timetable:', currentTimetable);
      
      // Validate required fields
      if (!currentTimetable.className) {
        alert('Please select a class first');
        return;
      }
      
      if (!currentTimetable.periods || currentTimetable.periods.length === 0) {
        alert('Please add at least one period');
        return;
      }
      
      if (!currentTimetable.subjects || currentTimetable.subjects.length === 0) {
        alert('Please add at least one subject');
        return;
      }
      
      const response = await fetch('/api/timetable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...currentTimetable,
          createdBy: userName,
          userRole: userRole
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error && errorData.error.includes('Access denied')) {
          alert('Access Denied: You need Admin Level 1 privileges to create/update timetables. Please contact your administrator.');
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const savedTimetable = await response.json();
      console.log('Timetable saved successfully:', savedTimetable);
      
      setCurrentTimetable(savedTimetable);
      setEditMode(false);
      alert('Timetable saved successfully!');
      
      if (userRole === 'admin1') {
        fetchAllTimetables();
      }
    } catch (error) {
      console.error('Error saving timetable:', error);
      alert('Error saving timetable: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const addSubject = () => {
    const newSubject = {
      code: '',
      name: '',
      teacherId: '',
      teacherName: ''
    };
    setCurrentTimetable(prev => ({
      ...prev,
      subjects: [...prev.subjects, newSubject]
    }));
  };

  const addPeriod = () => {
    const newPeriod = {
      name: `Period ${currentTimetable.periods.length + 1}`,
      startTime: '08:00',
      endTime: '08:45',
      duration: 45
    };
    setCurrentTimetable(prev => ({
      ...prev,
      periods: [...prev.periods, newPeriod]
    }));
  };

  const updatePeriod = (index, field, value) => {
    setCurrentTimetable(prev => {
      const updatedPeriods = [...prev.periods];
      updatedPeriods[index] = { ...updatedPeriods[index], [field]: value };
      
      // Auto-calculate duration when times change
      if (field === 'startTime' || field === 'endTime') {
        const period = updatedPeriods[index];
        if (period.startTime && period.endTime) {
          const [startHour, startMin] = period.startTime.split(':').map(Number);
          const [endHour, endMin] = period.endTime.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          updatedPeriods[index].duration = Math.max(0, endMinutes - startMinutes);
        }
      }
      
      return { ...prev, periods: updatedPeriods };
    });
  };

  const removePeriod = (index) => {
    if (currentTimetable.periods.length <= 1) {
      alert('At least one period is required');
      return;
    }
    
    setCurrentTimetable(prev => ({
      ...prev,
      periods: prev.periods.filter((_, i) => i !== index)
    }));
  };

  const removeSubject = (index) => {
    setCurrentTimetable(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index)
    }));
  };

  const updateSubject = (index, field, value) => {
    if (field === 'teacherId') {
      const teacher = teachers.find(t => t._id === value);
      setCurrentTimetable(prev => ({
        ...prev,
        subjects: prev.subjects.map((subject, i) => 
          i === index 
            ? { ...subject, teacherId: value, teacherName: teacher?.name || '' }
            : subject
        )
      }));
    } else {
      setCurrentTimetable(prev => ({
        ...prev,
        subjects: prev.subjects.map((subject, i) => 
          i === index ? { ...subject, [field]: value } : subject
        )
      }));
    }
  };

  const updateSchedule = (day, periodIndex, field, value) => {
    setCurrentTimetable(prev => {
      const newSchedule = { ...prev.schedule };
      if (!newSchedule[day]) newSchedule[day] = [];
      
      while (newSchedule[day].length <= periodIndex) {
        newSchedule[day].push({ period: '', subject: '', teacher: '', room: '' });
      }
      
      newSchedule[day][periodIndex] = {
        ...newSchedule[day][periodIndex],
        [field]: value
      };
      
      return { ...prev, schedule: newSchedule };
    });
  };

  const GridView = () => {
    const isInPopup = showMobilePopup;
    
    if (userRole === 'teacher') {
      // Teacher view - show a simplified grid of their classes
      return (
        <div className={`teacher-timetable-wrapper ${isInPopup ? 'mobile-popup-grid' : ''}`}>
          <div className="teacher-timetable-grid">
            <div className="grid-header">
              <div className="time-slot">Day</div>
              <div className="day-header">{isMobile && isInPopup ? 'Classes' : 'Your Classes'}</div>
            </div>
            
            {days.map((day, dayIndex) => (
              <div key={day} className="grid-row">
                <div className="time-slot">
                  <div className="period-name">{isMobile && isInPopup ? dayNames[dayIndex].substring(0, 3) : dayNames[dayIndex]}</div>
                </div>
                <div className="schedule-cell">
                  <div className="view-cell">
                    {currentTimetable?.schedule?.[day]?.length > 0 ? (
                      currentTimetable.schedule[day].map((item, index) => (
                        <div key={index} className="teacher-class-item">
                          <div className="subject-name">{item.subjectName}</div>
                          <div className="class-name">{isMobile && isInPopup ? item.className : `Class: ${item.className}`}</div>
                          {item.room && <div className="room-name">{isMobile && isInPopup ? item.room : `Room: ${item.room}`}</div>}
                        </div>
                      ))
                    ) : (
                      <div className="no-classes">No classes</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Student/Admin view - standard grid
    return (
      <div className={`timetable-grid-wrapper ${isInPopup ? 'mobile-popup-grid' : ''}`}>
        <div className="timetable-grid">
          <div className="grid-header">
            <div className="time-slot">Time</div>
            {dayNames.map(day => (
              <div key={day} className="day-header">{isMobile && isInPopup ? day.substring(0, 3) : day}</div>
            ))}
          </div>
          
          {currentTimetable?.periods?.map((period, periodIndex) => (
            <div key={periodIndex} className="grid-row">
              <div className="time-slot">
                <div className="period-name">{isMobile && isInPopup ? period.name.replace('Period ', 'P') : period.name}</div>
                <div className="period-time">{period.startTime} - {period.endTime}</div>
              </div>
              
              {days.map(day => (
                <div key={day} className="schedule-cell">
                  {editMode ? (
                    <div className="edit-cell">
                      <select
                        value={currentTimetable.schedule[day]?.[periodIndex]?.subject || ''}
                        onChange={(e) => updateSchedule(day, periodIndex, 'subject', e.target.value)}
                        className="subject-select"
                      >
                        <option value="">Select Subject</option>
                        {currentTimetable.subjects.map(subject => (
                          <option key={subject.code} value={subject.code}>
                            {subject.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Room"
                        value={currentTimetable.schedule[day]?.[periodIndex]?.room || ''}
                        onChange={(e) => updateSchedule(day, periodIndex, 'room', e.target.value)}
                        className="room-input"
                      />
                    </div>
                  ) : (
                    <div className="view-cell">
                      {currentTimetable.schedule[day]?.[periodIndex]?.subject && (
                        <>
                          <div className="subject-name">
                            {currentTimetable.subjects.find(s => s.code === currentTimetable.schedule[day][periodIndex].subject)?.name}
                          </div>
                          <div className="teacher-name">
                            {currentTimetable.subjects.find(s => s.code === currentTimetable.schedule[day][periodIndex].subject)?.teacherName}
                          </div>
                          {currentTimetable.schedule[day][periodIndex].room && (
                            <div className="room-name">{isMobile && isInPopup ? currentTimetable.schedule[day][periodIndex].room : `Room: ${currentTimetable.schedule[day][periodIndex].room}`}</div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const TodayView = () => {
    const today = days[new Date().getDay() - 1];
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    if (!today) {
      return (
        <div className="today-view">
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
            <h3 style={{ color: '#2196f3', marginBottom: '8px' }}>Today's Schedule</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>No classes scheduled for today</p>
          </div>
        </div>
      );
    }

    return (
      <div className="today-view">
        <div style={{ 
          background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
          color: 'white', 
          padding: '15px 20px', 
          borderRadius: '12px', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: '700' }}>
            📅 {dayNames[new Date().getDay() - 1]}
          </h3>
          <p style={{ margin: '0', fontSize: '14px', opacity: '0.9' }}>
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        
        <div style={{ marginTop: '25px' }}>
          <h4 style={{ 
            margin: '0 0 15px 0', 
            color: '#1e293b', 
            fontSize: '16px', 
            fontWeight: '700',
            padding: '8px 0',
            borderBottom: '2px solid #e2e8f0'
          }}>📋 Today's Classes</h4>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {userRole === 'teacher' ? (
              // Teacher view - show their classes for today
              currentTimetable?.schedule?.[today]?.length > 0 ? (
                currentTimetable.schedule[today].map((item, index) => {
                  const periodTime = currentTimetable.periods?.[index];
                  let isActive = false;
                  let isPast = false;
                  
                  if (periodTime) {
                    const [startHour, startMin] = periodTime.startTime.split(':').map(Number);
                    const [endHour, endMin] = periodTime.endTime.split(':').map(Number);
                    const startTime = startHour * 60 + startMin;
                    const endTime = endHour * 60 + endMin;
                    
                    isActive = currentTime >= startTime && currentTime <= endTime;
                    isPast = currentTime > endTime;
                  }

                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 15px',
                      borderRadius: '10px',
                      border: isActive ? '2px solid #16a34a' : '1px solid #e2e8f0',
                      background: isActive 
                        ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' 
                        : isPast 
                        ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                        : 'white',
                      opacity: isPast ? 0.7 : 1,
                      transform: isActive ? 'translateX(4px)' : 'none',
                      boxShadow: isActive ? '0 4px 12px rgba(34, 197, 94, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{ flex: '0 0 80px', textAlign: 'left' }}>
                        <div style={{ 
                          fontSize: '12px', 
                          fontWeight: '600', 
                          color: isActive ? '#15803d' : '#64748b',
                          marginBottom: '2px'
                        }}>
                          {item.period}
                        </div>
                        {periodTime && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: isActive ? '#15803d' : '#94a3b8',
                            fontWeight: '500'
                          }}>
                            {periodTime.startTime} - {periodTime.endTime}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ flex: 1, paddingLeft: '15px' }}>
                        <div style={{ 
                          fontSize: '15px', 
                          fontWeight: '700', 
                          color: isActive ? '#1e40af' : isPast ? '#64748b' : '#1e40af',
                          marginBottom: '2px'
                        }}>
                          {item.subjectName}
                        </div>
                        <div style={{ 
                          fontSize: '13px', 
                          color: isActive ? '#475569' : '#64748b',
                          fontWeight: '500'
                        }}>
                          Class: {item.className}
                        </div>
                        {item.room && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#64748b',
                            marginTop: '2px'
                          }}>
                            📍 {item.room}
                          </div>
                        )}
                      </div>
                      
                      {isActive && (
                        <div style={{ 
                          flex: '0 0 auto',
                          background: '#16a34a',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          Now
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                  No classes scheduled for today
                </div>
              )
            ) : (
              // Student/Admin view - show class schedule for today
              currentTimetable?.periods?.map((period, periodIndex) => {
                const scheduleItem = currentTimetable.schedule[today]?.[periodIndex];
                const subject = scheduleItem?.subject ? 
                  currentTimetable.subjects.find(s => s.code === scheduleItem.subject) : null;
                
                const [startHour, startMin] = period.startTime.split(':').map(Number);
                const [endHour, endMin] = period.endTime.split(':').map(Number);
                const startTime = startHour * 60 + startMin;
                const endTime = endHour * 60 + endMin;
                
                const isActive = currentTime >= startTime && currentTime <= endTime;
                const isPast = currentTime > endTime;
                const isUpcoming = currentTime < startTime;

                return (
                  <div key={periodIndex} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 15px',
                    borderRadius: '10px',
                    border: isActive ? '2px solid #16a34a' : '1px solid #e2e8f0',
                    background: isActive 
                      ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' 
                      : isPast 
                      ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                      : 'white',
                    opacity: isPast ? 0.7 : 1,
                    transform: isActive ? 'translateX(4px)' : 'none',
                    boxShadow: isActive ? '0 4px 12px rgba(34, 197, 94, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{ flex: '0 0 80px', textAlign: 'left' }}>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: '600', 
                        color: isActive ? '#15803d' : '#64748b',
                        marginBottom: '2px'
                      }}>
                        {period.name}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: isActive ? '#15803d' : '#94a3b8',
                        fontWeight: '500'
                      }}>
                        {period.startTime} - {period.endTime}
                      </div>
                    </div>
                    
                    <div style={{ flex: 1, paddingLeft: '15px' }}>
                      {subject ? (
                        <div>
                          <div style={{ 
                            fontSize: '15px', 
                            fontWeight: '700', 
                            color: isActive ? '#1e40af' : isPast ? '#64748b' : '#1e40af',
                            marginBottom: '2px'
                          }}>
                            {subject.name}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: isActive ? '#475569' : '#64748b',
                            fontWeight: '500'
                          }}>
                            {subject.teacherName}
                          </div>
                          {scheduleItem.room && (
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#64748b',
                              marginTop: '2px'
                            }}>
                              📍 {scheduleItem.room}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#94a3b8', 
                          fontStyle: 'italic'
                        }}>
                          Free Period
                        </div>
                      )}
                    </div>
                    
                    {isActive && (
                      <div style={{ 
                        flex: '0 0 auto',
                        background: '#16a34a',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        Now
                      </div>
                    )}
                    
                    {isUpcoming && currentTime > (startTime - 30) && (
                      <div style={{ 
                        flex: '0 0 auto',
                        background: '#f59e0b',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        Soon
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  const ListView = () => (
    <div className="timetable-list">
      {days.map((day, dayIndex) => (
        <div key={day} className="day-section">
          <h3 className="day-title">{dayNames[dayIndex]}</h3>
          <div className="day-schedule">
            {userRole === 'teacher' ? (
              // Teacher view - show their classes
              currentTimetable?.schedule?.[day]?.map((item, index) => (
                <div key={index} className="schedule-item">
                  <div className="time-info">
                    <span className="period">{item.period}</span>
                  </div>
                  <div className="subject-info">
                    <span className="subject">{item.subjectName}</span>
                    <span className="teacher">Class: {item.className}</span>
                    {item.room && <span className="room">Room: {item.room}</span>}
                  </div>
                </div>
              ))
            ) : (
              // Student/Admin view - show class schedule
              currentTimetable?.periods?.map((period, periodIndex) => {
                const scheduleItem = currentTimetable.schedule[day]?.[periodIndex];
                if (!scheduleItem?.subject) return null;
                
                const subject = currentTimetable.subjects.find(s => s.code === scheduleItem.subject);
                
                return (
                  <div key={periodIndex} className="schedule-item">
                    <div className="time-info">
                      <span className="period">{period.name}</span>
                      <span className="time">{period.startTime} - {period.endTime}</span>
                    </div>
                    <div className="subject-info">
                      <span className="subject">{subject?.name}</span>
                      <span className="teacher">{subject?.teacherName}</span>
                      {scheduleItem.room && <span className="room">Room: {scheduleItem.room}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading && !currentTimetable) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <p style={{ color: '#2196f3', fontSize: '16px', fontWeight: '600' }}>Loading your timetable...</p>
      </div>
    );
  }

  if (!currentTimetable && !loading) {
    return (
      <div style={{ padding: '20px' }}>
        {!embedded && userRole === 'admin1' && (
          <div className="manager-header">
            <h2>Weekly Timetable Management</h2>
            
            <div style={{ 
              marginBottom: '15px', 
              padding: '12px', 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', 
              borderRadius: '8px', 
              border: '2px solid #4caf50' 
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>⚡ Admin Level 1 Timetable Control</h4>
              <p style={{ margin: '0', fontSize: '14px', color: '#2e7d32' }}>
                Create and manage weekly timetables for all classes. Set periods, assign subjects and teachers, configure room allocations.
              </p>
            </div>
            
            <div className="controls">
              <div className="class-selector">
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls._id} value={cls.name}>{cls.name}</option>
                  ))}
                </select>
                <button 
                  onClick={() => fetchTimetable(selectedClass)} 
                  disabled={!selectedClass || loading}
                  style={{ opacity: (!selectedClass || loading) ? 0.5 : 1 }}
                >
                  {loading ? 'Loading...' : 'Load Timetable'}
                </button>
                <button 
                  onClick={createNewTimetable} 
                  disabled={!selectedClass || loading}
                  style={{ opacity: (!selectedClass || loading) ? 0.5 : 1 }}
                >
                  Create New
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
          <h4 style={{ color: '#2196f3', marginBottom: '8px' }}>No Timetable Available</h4>
          {userRole === 'admin1' ? (
            <>
              <p style={{ color: '#2196f3', fontSize: '14px' }}>Select a class above and create a new timetable or load an existing one.</p>
              <p style={{ color: '#28a745', fontSize: '12px', fontWeight: 'bold' }}>✅ You have Admin Level 1 privileges to create and manage timetables.</p>
            </>
          ) : (
            <p style={{ color: '#2196f3', fontSize: '14px' }}>Your timetable hasn't been created yet. Please contact your administrator.</p>
          )}
          {!embedded && userRole === 'teacher' && (
            <button 
              onClick={() => fetchTimetable('')} 
              style={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginTop: '15px'
              }}
            >
              Refresh Timetable
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`timetable-manager ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <div className="manager-header">
          <h2>Weekly Timetable Management</h2>
          
          {userRole === 'admin1' && (
            <div style={{ 
              marginBottom: '15px', 
              padding: '12px', 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', 
              borderRadius: '8px', 
              border: '2px solid #4caf50' 
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>⚡ Admin Level 1 Timetable Control</h4>
              <p style={{ margin: '0', fontSize: '14px', color: '#2e7d32' }}>
                Create and manage weekly timetables for all classes. Set periods, assign subjects and teachers, configure room allocations.
              </p>
            </div>
          )}
          
          <div className="controls">
            {userRole === 'admin1' && (
              <div className="class-selector">
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls._id} value={cls.name}>{cls.name}</option>
                  ))}
                </select>
                <button 
                  onClick={() => fetchTimetable(selectedClass)} 
                  disabled={!selectedClass || loading}
                  style={{ opacity: (!selectedClass || loading) ? 0.5 : 1 }}
                >
                  {loading ? 'Loading...' : 'Load Timetable'}
                </button>
                <button 
                  onClick={createNewTimetable} 
                  disabled={!selectedClass || loading}
                  style={{ opacity: (!selectedClass || loading) ? 0.5 : 1 }}
                >
                  Create New
                </button>
              </div>
            )}
            
            <div className="view-controls">
              <button
                className={viewMode === 'today' ? 'active' : ''}
                onClick={() => setViewMode('today')}
              >
                Today View
              </button>
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
              >
                Grid View
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                List View
              </button>
            </div>
          </div>
        </div>
      )}

      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <span style={{ color: '#2196f3', fontSize: '16px', fontWeight: '600' }}>
            {userRole === 'teacher' ? 'Teaching Schedule' : `Class: ${currentTimetable?.className}`}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setViewMode('today')}
              style={{
                background: viewMode === 'today' ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' : '#f5f5f5',
                color: viewMode === 'today' ? 'white' : '#666',
                border: 'none',
                padding: isMobile ? '8px 16px' : '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                boxShadow: viewMode === 'today' ? '0 2px 8px rgba(33, 150, 243, 0.3)' : 'none'
              }}
            >
              📅 Today
            </button>
            <button
              onClick={() => isMobile ? setShowMobilePopup(true) : setViewMode('grid')}
              style={{
                background: viewMode === 'grid' ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' : '#f5f5f5',
                color: viewMode === 'grid' ? 'white' : '#666',
                border: 'none',
                padding: isMobile ? '8px 16px' : '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(33, 150, 243, 0.3)' : 'none'
              }}
            >
              {isMobile ? '📊 Week' : '📊 Full Week'}
            </button>
          </div>
        </div>
      )}

      {currentTimetable && (
        <div className="timetable-content">
          <div className="timetable-info">
            <h3>{currentTimetable.className} - {currentTimetable.academicYear}</h3>
            {userRole === 'admin1' && (
              <div className="edit-controls">
                {editMode ? (
                  <>
                    <button onClick={saveTimetable} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Timetable'}
                    </button>
                    <button onClick={() => setEditMode(false)}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setEditMode(true)}>Edit Timetable</button>
                )}
              </div>
            )}
          </div>

          {editMode && userRole === 'admin1' && (
            <>
              <div className="periods-manager">
                <h4>📅 Periods & Timings</h4>
                <div className="periods-list">
                  {currentTimetable.periods.map((period, index) => (
                    <div key={index} className="period-row">
                      <input
                        type="text"
                        placeholder="Period Name"
                        value={period.name}
                        onChange={(e) => updatePeriod(index, 'name', e.target.value)}
                        className="period-name-input"
                      />
                      <input
                        type="time"
                        value={period.startTime}
                        onChange={(e) => updatePeriod(index, 'startTime', e.target.value)}
                        className="time-input"
                      />
                      <input
                        type="time"
                        value={period.endTime}
                        onChange={(e) => updatePeriod(index, 'endTime', e.target.value)}
                        className="time-input"
                      />
                      <span className="duration-display">
                        {period.duration} min
                      </span>
                      <button 
                        onClick={() => removePeriod(index)}
                        className="remove-period"
                        title="Remove Period"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button onClick={addPeriod} className="add-period">
                    ➕ Add Period
                  </button>
                </div>
              </div>

              <div className="subjects-manager">
                <h4>📚 Subjects & Teachers</h4>
                <div className="subjects-list">
                  {currentTimetable.subjects.map((subject, index) => (
                    <div key={index} className="subject-row">
                      <input
                        type="text"
                        placeholder="Subject Code"
                        value={subject.code}
                        onChange={(e) => updateSubject(index, 'code', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Subject Name"
                        value={subject.name}
                        onChange={(e) => updateSubject(index, 'name', e.target.value)}
                      />
                      <select
                        value={subject.teacherId}
                        onChange={(e) => updateSubject(index, 'teacherId', e.target.value)}
                      >
                        <option value="">Select Teacher</option>
                        {teachers.map(teacher => (
                          <option key={teacher._id} value={teacher._id}>
                            {teacher.name}
                          </option>
                        ))}
                      </select>
                      <button 
                        onClick={() => removeSubject(index)}
                        className="remove-subject"
                        title="Remove Subject"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                  <button onClick={addSubject} className="add-subject">
                    ➕ Add Subject
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="timetable-display">
            {viewMode === 'today' && <TodayView />}
            {viewMode === 'grid' && <GridView />}
            {viewMode === 'list' && <ListView />}
          </div>
          
          {showMobilePopup && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.8)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white',
                padding: '12px 15px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>📅 {userRole === 'teacher' ? 'Teaching Schedule' : currentTimetable?.className}</h3>
                <button
                  onClick={() => setShowMobilePopup(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{
                flex: 1,
                background: 'white',
                overflow: 'auto',
                padding: isMobile ? '10px' : '20px',
                minHeight: 0
              }}>
                <GridView />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimetableManager;